import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import scanQr from '../../../../assets/images/scan-qr.svg';
import './cameraOnBoarding.css';
import ChaloButton from '../../../../shared/widgets/chaloButton';
import ChaloAlertDialog from '../../../../shared/widgets/chaloAlertDialog';
import ChaloInputField from '../../../../shared/widgets/chaloInputField';
import ChaloLoader from '../../../../shared/widgets/chaloLoader';
import ChaloDropDown from '../../../../shared/widgets/chaloDropDown';
import { fetchCameraBrands, fetchLiveFeedDetails } from '../../../../services/vehicleMasterService';
import { AuthenticationService } from '../../../../services/authorization';
import PropTypes from 'prop-types';

const getFailureReason = (data) => {
    if (data?.description) {
        return { heading: 'Error', message: data.description };
    } else if (data?.error) {
        return { heading: 'Error', message: data.error };
    } else if (data?.msg === "Failed to fetch") {
        return { heading: 'Error', message: 'Network unavailable. Please turn on mobile data or connect to Wi-Fi' };
    } else {
        return { heading: 'Error', message: 'Something went wrong. Please try again later.' };
    }
};

const isCameraIdValid = (value) =>
  /^[a-zA-Z0-9-]+$/.test(value.trim());


const QRSection = React.memo(({ onScanClick }) => (
    <div className="scan-qr-content">
        <img src={scanQr} alt="Scan QR Code" className="img-responsive" />
        <p>Scan QR code placed on the camera</p>
        <ChaloButton buttonText="Scan QR Code" onClick={onScanClick} />
    </div>
));

QRSection.propTypes = {
    onScanClick: PropTypes.func,
};

const ManualInputForm = ({ cameraId, onInputChange, onSubmit }) => {
    const isButtonDisabled = !isCameraIdValid(cameraId);
    return (
        <div className="camera-id-form">
            <p>Or enter the camera ID manually</p>
            <div className="id-input-field">
                <ChaloInputField
                    label="Camera ID"
                    placeholder="Camera ID"
                    name="cameraID"
                    value={cameraId}
                    onChange={onInputChange}
                    id="camera-id"
                />
            </div>
            <ChaloButton
                buttonText="Continue"
                onClick={onSubmit}
                disabled={isButtonDisabled}
            />
        </div>
    );
};

ManualInputForm.propTypes = {
    cameraId: PropTypes.string,
    onInputChange: PropTypes.func,
    onSubmit: PropTypes.func,
};

const CameraBrandInput = ({ cameraBrand, onInputChange, options }) => (
    <div className="camera-id-form">
        <p>Select Camera Brand</p>
        <div className="id-input-field">
            <ChaloDropDown
                label="Camera Brand"
                options={options}
                value={cameraBrand}
                name="cameraBrand"
                onChange={onInputChange}
                id="camera-brand"
            />
        </div>
    </div>
);

CameraBrandInput.propTypes = {
    cameraBrand: PropTypes.string,
    onInputChange: PropTypes.func,
    options: PropTypes.array,
};

const ErrorDialog = ({ heading, message, onClose }) => (
    <div className="error-dialog-container">
        <ChaloAlertDialog heading={heading} message={message} onClose={onClose} />
    </div>
);

ErrorDialog.propTypes = {
    heading: PropTypes.string,
    message: PropTypes.string,
    onClose: PropTypes.func,
};

const CreatingCamera = () => {
    const navigate = useNavigate();
    const [cameraId, setCameraId] = useState('');
    const [cameraBrand, setCameraBrand] = useState('');
    const [cameraBrandOptions, setCameraBrandOptions] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [errorHeading, setErrorHeading] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const authService = AuthenticationService.getInstance();
    
    const parentRoute = authService.getMasterParentRoute();

    useEffect(() => {
        const getCameraBrands = async () => {
            try {
                const brands = await fetchCameraBrands();
                setCameraBrandOptions(
                    brands.map((b) => ({ label: b, value: b }))
                );
            } catch (err) {
                console.error('Error fetching camera brands:', err);
            }
        };
        getCameraBrands();
    }, []);

    const handleNavigation = () => {
        const path = `${parentRoute}/scanCameraQr`;

        navigate(path, { state: { cameraBrand } });
    };

    const handleInputChange = useCallback((event) => {
        const value = event.target.value.replace(/[^a-zA-Z0-9-]/g, '');
        setCameraId(value);
    }, []);


    const handleBrandChange = useCallback((event) => {
        setCameraBrand(event.target.value);
    }, []);

    const handleCameraIdSubmit = async () => {
        setIsLoading(true);
        try {
            const data = await fetchLiveFeedDetails(cameraId, cameraBrand);
            setIsLoading(false);

            let combinedData = { ...data, cameraId, cameraBrand };

            if (data.cameraConnected) {
                const path = `${parentRoute}/liveFeedPreview`;

                navigate(path, { state: { feedData: combinedData, cameraConnected: true } });
            } else {
                redirectToCameraDetails(cameraId, cameraBrand);
            }
        } catch (error) {
            setIsLoading(false);
            handleFetchError(error, cameraId, cameraBrand);
        }
    };

    const redirectToCameraDetails = (cameraId, cameraBrand) => {
        const path = `${parentRoute}/cameraDetails`;

        navigate(path, { state: { liveFeed: false, cameraId, cameraBrand, cameraConnected: false } });
    };

    const handleFetchError = (error, cameraId, cameraBrand) => {
        if (error?.status === 400) {
            redirectToCameraDetails(cameraId, cameraBrand);
            return;
        }

        const failureReason = typeof error === 'object'
            ? getFailureReason(error)
            : { heading: 'Network Error', message: 'There was a problem connecting to the API.' };

        setErrorHeading(failureReason.heading);
        setErrorMessage(failureReason.message);
        setShowModal(true);
    };

    const closeModal = useCallback(() => {
        setShowModal(false);
        setErrorMessage('');
        setErrorHeading('');
    }, []);

    return (
        <div className="camera-creater-wrapper">
            {isLoading && <ChaloLoader />}
            <div className="col-md-12">
                <div className="add-camera-container">
                    <h3>Camera Onboarding</h3>
                    <div className="add-camera-content">
                        <CameraBrandInput
                            cameraBrand={cameraBrand}
                            onInputChange={handleBrandChange}
                            options={cameraBrandOptions}
                        />
                        {cameraBrand && (
                        <>
                            <hr className="custom-hr" />

                        <QRSection onScanClick={handleNavigation} />

                        <hr className="custom-hr" />

                        <ManualInputForm
                            cameraId={cameraId}
                            onInputChange={handleInputChange}
                            onSubmit={handleCameraIdSubmit}
                        /> </>)}
                    </div>
                </div>
                {showModal && (
                    <ErrorDialog
                        heading={errorHeading}
                        message={errorMessage}
                        onClose={closeModal}
                    />
                )}
            </div>
        </div>
    );
};

export default CreatingCamera;
